import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IGuest, ISeatingPlan, ITable, IPlanner } from '../interface'

// localStorage persistence for seating auth
const SEATING_STORAGE_KEY = '@seatingAuth'
const ONBOARDING_STORAGE_KEY = '@seatingOnboarding'

const loadOnboardingCompleted = (): boolean => {
	try {
		return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true'
	} catch {
		return false
	}
}

const saveOnboardingCompleted = () => {
	try {
		localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true')
	} catch (e) {
		console.error('Failed to save onboarding status:', e)
	}
}

interface ICanvasView {
	zoom: number;
	panX: number;
	panY: number;
}

// Only store auth data, not table positions or seating assignments
interface AuthStorageData {
	authenticatedPlanner: IPlanner;
	timestamp: number;
}

const saveAuthToStorage = (data: AuthStorageData) => {
	try {
		localStorage.setItem(SEATING_STORAGE_KEY, JSON.stringify(data))
	} catch (e) {
		console.error('Failed to save seating auth:', e)
	}
}

// Before the planners-collection rename, this same key was written as
// `{ authenticatedWedding: {...} }`. Every couple with a session inside the
// 24-hour window at deploy time has exactly that sitting in their browser,
// and its id means nothing under the new rules — so it must not be trusted
// as `authenticatedPlanner` just because the key matches. Checked here, once,
// so every caller of loadAuthFromStorage gets a clean `null` for it instead
// of having to guard the shape itself.
const isValidAuthStorageData = (value: unknown): value is AuthStorageData => {
	const candidate = value as Partial<AuthStorageData> | null
	return !!candidate &&
		typeof candidate.timestamp === 'number' &&
		!!candidate.authenticatedPlanner &&
		typeof candidate.authenticatedPlanner.id === 'string'
}

export const loadAuthFromStorage = (): AuthStorageData | null => {
	try {
		const data = localStorage.getItem(SEATING_STORAGE_KEY)
		if (!data) return null
		const parsed: unknown = JSON.parse(data)

		// A legacy or otherwise unrecognised shape is not a session — clear it
		// so it can't trip the next read either, and let the caller fall
		// through to the PIN form exactly as if nothing were stored.
		if (!isValidAuthStorageData(parsed)) {
			localStorage.removeItem(SEATING_STORAGE_KEY)
			return null
		}

		// Session expires after 24 hours
		if (Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
			localStorage.removeItem(SEATING_STORAGE_KEY)
			return null
		}
		return parsed
	} catch (e) {
		return null
	}
}

const clearSeatingStorage = () => {
	localStorage.removeItem(SEATING_STORAGE_KEY)
}

interface ISeatingState {
	authenticatedPlanner: IPlanner | null;
	guests: IGuest[];
	seatingPlan: ISeatingPlan | null;
	loading: boolean;
	error: string | null;
	pendingChanges: boolean;
	canvasView: ICanvasView;
	// Onboarding state
	hasCompletedOnboarding: boolean;
	onboardingStep: number; // 0 = not started, 1-4 = active steps
}

const initialState: ISeatingState = {
	authenticatedPlanner: null,
	guests: [],
	seatingPlan: null,
	loading: false,
	error: null,
	pendingChanges: false,
	canvasView: {
		zoom: 0.45,
		panX: 0,
		panY: 0
	},
	hasCompletedOnboarding: loadOnboardingCompleted(),
	onboardingStep: 0
}

const seatingSlice = createSlice({
	name: 'seating',
	initialState,
	reducers: {
		// Auth actions
		setAuthenticatedPlanner: (state: ISeatingState, action: PayloadAction<IPlanner>) => {
			state.authenticatedPlanner = action.payload
			state.error = null
		},
		clearAuth: (state: ISeatingState) => {
			state.authenticatedPlanner = null
			state.guests = []
			state.seatingPlan = null
			state.pendingChanges = false
			state.error = null
			clearSeatingStorage()
		},
		saveSession: (state: ISeatingState) => {
			if (state.authenticatedPlanner) {
				saveAuthToStorage({
					authenticatedPlanner: state.authenticatedPlanner,
					timestamp: Date.now()
				})
			}
		},

		// Loading/Error actions
		setLoading: (state: ISeatingState, action: PayloadAction<boolean>) => {
			state.loading = action.payload
		},
		setError: (state: ISeatingState, action: PayloadAction<string | null>) => {
			state.error = action.payload
		},

		// Guest actions
		setGuests: (state: ISeatingState, action: PayloadAction<IGuest[]>) => {
			state.guests = action.payload
		},
		addGuest: (state: ISeatingState, action: PayloadAction<IGuest>) => {
			state.guests.push(action.payload)
			state.pendingChanges = true
		},
		updateGuest: (state: ISeatingState, action: PayloadAction<IGuest>) => {
			const index = state.guests.findIndex(g => g.id === action.payload.id)
			if (index !== -1) {
				state.guests[index] = action.payload
				state.pendingChanges = true
			}
		},
		removeGuest: (state: ISeatingState, action: PayloadAction<string>) => {
			state.guests = state.guests.filter(g => g.id !== action.payload)
			state.pendingChanges = true
		},
		assignGuestToTable: (state: ISeatingState, action: PayloadAction<{ guestId: string; tableId: string; seatPosition?: number }>) => {
			const guest = state.guests.find(g => g.id === action.payload.guestId)
			if (guest) {
				guest.tableId = action.payload.tableId
				guest.seatPosition = action.payload.seatPosition
				state.pendingChanges = true
			}
		},
		unassignGuest: (state: ISeatingState, action: PayloadAction<string>) => {
			const guest = state.guests.find(g => g.id === action.payload)
			if (guest) {
				guest.tableId = undefined
				guest.seatPosition = undefined
				state.pendingChanges = true
			}
		},
		updateGuestStatus: (state: ISeatingState, action: PayloadAction<{ guestId: string; status: 'pending' | 'confirmed' | 'declined' }>) => {
			const guest = state.guests.find(g => g.id === action.payload.guestId)
			if (guest) {
				guest.rsvpStatus = action.payload.status
				state.pendingChanges = true
			}
		},

		// Seating plan actions
		setSeatingPlan: (state: ISeatingState, action: PayloadAction<ISeatingPlan | null>) => {
			state.seatingPlan = action.payload
		},

		// Table actions
		addTable: (state: ISeatingState, action: PayloadAction<ITable>) => {
			if (state.seatingPlan) {
				state.seatingPlan.tables.push(action.payload)
				state.pendingChanges = true
			}
		},
		updateTable: (state: ISeatingState, action: PayloadAction<ITable>) => {
			if (state.seatingPlan) {
				const index = state.seatingPlan.tables.findIndex(t => t.id === action.payload.id)
				if (index !== -1) {
					state.seatingPlan.tables[index] = action.payload
					state.pendingChanges = true
				}
			}
		},
		updateTablePosition: (state: ISeatingState, action: PayloadAction<{ tableId: string; position: { x: number; y: number } }>) => {
			if (state.seatingPlan) {
				const table = state.seatingPlan.tables.find(t => t.id === action.payload.tableId)
				if (table) {
					table.position = action.payload.position
					state.pendingChanges = true
				}
			}
		},
		removeTable: (state: ISeatingState, action: PayloadAction<string>) => {
			if (state.seatingPlan) {
				state.seatingPlan.tables = state.seatingPlan.tables.filter(t => t.id !== action.payload)
				// Unassign guests from removed table
				state.guests.forEach(guest => {
					if (guest.tableId === action.payload) {
						guest.tableId = undefined
						guest.seatPosition = undefined
					}
				})
				state.pendingChanges = true
			}
		},

		// Clear pending changes flag after save
		clearPendingChanges: (state: ISeatingState) => {
			state.pendingChanges = false
		},

		// Canvas view actions (zoom/pan)
		setZoom: (state: ISeatingState, action: PayloadAction<number>) => {
			state.canvasView.zoom = Math.max(0.25, Math.min(3.0, action.payload))
		},
		setPan: (state: ISeatingState, action: PayloadAction<{ x: number; y: number }>) => {
			state.canvasView.panX = action.payload.x
			state.canvasView.panY = action.payload.y
		},
		setCanvasView: (state: ISeatingState, action: PayloadAction<{ zoom: number; panX: number; panY: number }>) => {
			state.canvasView.zoom = Math.max(0.25, Math.min(3.0, action.payload.zoom))
			state.canvasView.panX = action.payload.panX
			state.canvasView.panY = action.payload.panY
		},
		resetCanvasView: (state: ISeatingState) => {
			state.canvasView = { zoom: 0.45, panX: 0, panY: 0 }
		},

		// Onboarding actions
		startOnboarding: (state: ISeatingState) => {
			if (!state.hasCompletedOnboarding) {
				state.onboardingStep = 1
			}
		},
		nextOnboardingStep: (state: ISeatingState) => {
			state.onboardingStep += 1
			if (state.onboardingStep > 4) {
				state.hasCompletedOnboarding = true
				state.onboardingStep = 0
				saveOnboardingCompleted()
			}
		},
		skipOnboarding: (state: ISeatingState) => {
			state.hasCompletedOnboarding = true
			state.onboardingStep = 0
			saveOnboardingCompleted()
		}
	}
})

export const {
	setAuthenticatedPlanner,
	clearAuth,
	saveSession,
	setLoading,
	setError,
	setGuests,
	addGuest,
	updateGuest,
	removeGuest,
	assignGuestToTable,
	unassignGuest,
	updateGuestStatus,
	setSeatingPlan,
	addTable,
	updateTable,
	updateTablePosition,
	removeTable,
	clearPendingChanges,
	setZoom,
	setPan,
	setCanvasView,
	resetCanvasView,
	startOnboarding,
	nextOnboardingStep,
	skipOnboarding
} = seatingSlice.actions

export default seatingSlice.reducer
